/**
 * Nova organização: criar um cliente sem terminal (01/10/2026).
 *
 * Antes, criar uma organização era copiar o token da sessão pelo F12, montar
 * um JSON com a chave do cadastro dentro e mandar por `curl`, depois de criar
 * o espaço no painel do cadastro por um túnel. Agora a tela chama a rota com a
 * sessão de quem está logado, e a API cria o espaço sozinha (01/10/2026).
 *
 * Só o Super Admin vê esta tela (emenda de 21/10 ao ADR-0025). A API confere
 * de novo: o item do menu esconder não é a guarda
 * (`provisionamento/permissao.ts`).
 *
 * O que ainda é manual fica escrito no resultado, como a API devolve: a tela
 * não promete uma organização pronta quando ela não está.
 *
 * ── Com contrato (#769) ───────────────────────────────────────────────────
 *
 * A criação pede o cadastro da empresa, o contrato do catálogo e o contato
 * financeiro, no `FormularioComContrato`. Esta tela continua dona do resto: o
 * progresso, as perguntas (a conta que já existe e a organização encerrada), o
 * "Tentar de novo" com o identificador travado, e o resultado.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ErroDaApi,
  SEGMENTOS_DA_ORGANIZACAO,
  type RevendaCompletada,
  type RevendaProvisionada,
} from '@cliqcrm/contracts';
import { Avatar, Botao, CabecalhoDeTela, Campo, type OpcaoDeEscolha } from '@/shared/ui';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi, usePlanos } from '@/shared/api/consultas';
import { useSessao } from '@/shared/sessao/contexto';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { PrimeiroAcessoDaPessoa } from '@/entities/acesso/pessoa';
import { podeCriarOrganizacaoComo } from '@/entities/acesso/papel';
import {
  corpoDaCriacao,
  hojeLocal,
  planosParaEscolher,
  type ConfirmacoesDaCriacao,
} from '../lib/corpo-da-criacao';
import type { NovaOrganizacaoComContrato } from '../lib/formulario-com-contrato';
import { FormularioComContrato } from './formulario-com-contrato';
import './operacao.css';

/** Os segmentos do contrato, os mesmos da venda online (#564). */
const SEGMENTOS: OpcaoDeEscolha[] = SEGMENTOS_DA_ORGANIZACAO.map((segmento) => ({
  valor: segmento.valor,
  rotulo: segmento.nome,
}));

/** As perguntas que o servidor faz antes de criar. */
type Pergunta = keyof ConfirmacoesDaCriacao;
type Perguntas = Partial<Record<Pergunta, string>>;

/** O campo do pedido que confirma cada pergunta: é por ele que o erro diz qual é. */
const CAMPO_DA_PERGUNTA: Readonly<Record<Pergunta, string>> = {
  contaExistente: 'confirmarContaExistente',
  retomadaDeEncerrada: 'confirmarRetomadaDeEncerrada',
};

/** O texto, quando o servidor não manda um. */
const TEXTO_DA_PERGUNTA: Readonly<Record<Pergunta, string>> = {
  contaExistente: 'Este e-mail já tem conta no CliqCRM, em outra organização.',
  retomadaDeEncerrada: 'Este identificador é de uma organização encerrada.',
};

/** Qual pergunta veio: pelo campo do erro; sem campo (o servidor de antes), a da conta. */
function perguntaDa(falha: ErroDaApi): Pergunta {
  const campos = (falha.problema.errors ?? []).map((erro) => erro.campo);
  return campos.includes(CAMPO_DA_PERGUNTA.retomadaDeEncerrada)
    ? 'retomadaDeEncerrada'
    : 'contaExistente';
}

/**
 * O texto do botão. Depois de uma pergunta, o clique é o que a confirma, e não
 * o primeiro repetido: ele dá a administração a uma conta que já existe, ou
 * continua a organização encerrada.
 */
function textoDoEnvio(travado: boolean, perguntas: Perguntas): string {
  if (travado) return 'Tentar de novo';
  const conta = perguntas.contaExistente !== undefined;
  if (perguntas.retomadaDeEncerrada !== undefined) {
    return conta ? 'Continuar com esta conta' : 'Continuar a organização encerrada';
  }
  return conta ? 'Criar com esta conta' : 'Criar organização';
}

/** Os quatro passos da criação: o cartão de apresentação, o progresso e o resumo. */
const PASSOS = [
  { nome: 'Acesso', texto: 'O espaço da organização no login, com o identificador no endereço.' },
  { nome: 'Cadastro', texto: 'O CRM da organização, com o funil padrão e as etapas.' },
  { nome: 'Atendimento', texto: 'A caixa de conversas, pronta para receber o primeiro canal.' },
  {
    nome: 'Administrador',
    texto: 'A primeira pessoa, com a senha provisória para o primeiro acesso.',
  },
] as const;

/** O visto dos passos concluídos. */
function Visto(): ReactNode {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
    </svg>
  );
}

/**
 * Os segundos desde que a criação começou.
 *
 * A API responde uma vez, no fim: não há progresso por passo para mostrar, e a
 * tela não inventa um — marcar "Acesso ✓" sem saber seria simular sucesso. O
 * que é verdade enquanto espera é o tempo correndo, e é ele que aparece.
 */
function useSegundosDesde(ligado: boolean): number {
  const [segundos, definirSegundos] = useState(0);
  useEffect(() => {
    if (!ligado) return;
    definirSegundos(0);
    const inicio = Date.now();
    const relogio = window.setInterval(
      () => definirSegundos(Math.floor((Date.now() - inicio) / 1000)),
      1000,
    );
    return () => window.clearInterval(relogio);
  }, [ligado]);
  return segundos;
}

export function NovaOrganizacao({
  aoVoltar,
  aoIrParaPlanos,
}: {
  /** Volta para a lista de organizações; sem ele, não há o botão de cancelar. */
  aoVoltar?: () => void;
  /** Abre o catálogo de planos, quando não há plano para contratar. */
  aoIrParaPlanos?: () => void;
} = {}): ReactNode {
  const api = useApi();
  const sessao = useSessao();
  const consultaDosPlanos = usePlanos();
  const planos = useMemo(
    () => planosParaEscolher(consultaDosPlanos.data?.itens ?? []),
    [consultaDosPlanos.data],
  );
  // "Começar outra" troca a chave: o formulário volta vazio.
  const [versao, definirVersao] = useState(0);
  // O que foi pedido por último: o resumo do resultado e o "Criando…" leem daqui.
  const [pedido, definirPedido] = useState<NovaOrganizacaoComContrato | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  /*
   * As perguntas da criação, com o texto que o servidor mandou: o e-mail do
   * administrador já tem conta no CliqCRM (08/10/2026), e o identificador é de
   * uma organização encerrada, que a criação continuaria (#1118).
   *
   * São perguntas, e não recusas, como no convite de pessoas: o próximo clique
   * confirma as que estão na tela. Em vermelho, junto dos erros, diriam "deu
   * errado". Trocar o e-mail desfaz a da conta; trocar o identificador, a da
   * encerrada.
   */
  const [perguntas, definirPerguntas] = useState<Perguntas>({});
  const [resultado, definirResultado] = useState<RevendaProvisionada | null>(null);
  // A criação pode ter começado: houve resposta parcial, ou uma falha que não
  // é recusa do pedido (rede, servidor). Recusa 4xx não criou nada.
  const [podeTerComecado, definirPodeTerComecado] = useState(false);

  /*
   * Depois de uma criação que pode ter começado, nome e identificador ficam
   * travados. "Tentar de novo" só continua de onde parou com o MESMO
   * identificador; um outro criaria uma segunda organização e deixaria a
   * primeira presa. "Começar outra" destrava, dizendo o que fica para trás.
   */
  const travado = podeTerComecado;
  /*
   * O cadastro sem vaga: tentar de novo recebe a mesma recusa até alguém
   * liberar espaço ou mudar a licença. A tela não oferece repetir.
   */
  const semVaga =
    resultado?.status !== 'ativo' &&
    (resultado?.pendencias.some((p) => p.includes('limite de espaços')) ?? false);

  const [enviando, definirEnviando] = useState(false);
  const segundos = useSegundosDesde(enviando);

  /** O campo que a pergunta citava mudou: ela não vale mais. */
  function esquecerPergunta(pergunta: Pergunta): void {
    definirPerguntas((antes) => {
      if (antes[pergunta] === undefined) return antes;
      const { [pergunta]: _, ...resto } = antes;
      return resto;
    });
  }

  // O mesmo padrão do botão de feedback: a chamada e a falha tratadas aqui.
  async function criar(dados: NovaOrganizacaoComContrato): Promise<void> {
    if (!api) return;
    definirPedido(dados);
    definirErro(null);
    // O motivo da tentativa anterior não vale para esta.
    definirResultado(null);
    definirEnviando(true);
    try {
      // O endereço desta instalação, para onde o login da organização nova
      // volta. O servidor usa o que vier aqui; quem cria é o Super Admin.
      const saida = await api.provisionarRevenda({
        corpo: corpoDaCriacao(
          dados,
          planos.find((plano) => plano.id === dados.planoId),
          window.location.origin,
          {
            contaExistente: perguntas.contaExistente !== undefined,
            retomadaDeEncerrada: perguntas.retomadaDeEncerrada !== undefined,
          },
        ),
      });
      definirResultado(saida);
      definirPodeTerComecado(true);
    } catch (falha) {
      // A pergunta vem antes de a organização nascer: nada começou.
      if (falha instanceof ErroDaApi && falha.codigo === 'CONFIRMACAO_EXIGIDA') {
        const pergunta = perguntaDa(falha);
        definirPerguntas((antes) => ({
          ...antes,
          [pergunta]: falha.problema.detail ?? TEXTO_DA_PERGUNTA[pergunta],
        }));
        return;
      }
      if (!(falha instanceof ErroDaApi && falha.status < 500)) definirPodeTerComecado(true);
      definirErro(
        recadoDeErro(falha, { generica: 'Não foi possível criar a organização. Tente de novo.' }),
      );
    } finally {
      definirEnviando(false);
    }
  }

  function recomecar(): void {
    definirResultado(null);
    definirPodeTerComecado(false);
    definirErro(null);
    definirPedido(null);
    definirPerguntas({});
    definirVersao((antes) => antes + 1);
  }

  const concluida = resultado?.status === 'ativo';

  if (concluida && resultado && pedido) {
    return (
      <div className="operacao">
        {/*
          A ação de criar outra é da TELA, e por isso mora no cabeçalho — e não
          dentro do cartão da organização que acabou de nascer, onde parecia
          uma ação sobre ela.
        */}
        <CabecalhoDeTela
          titulo="Nova organização"
          acoes={
            <Botao variante="primario" onClick={recomecar}>
              Criar outra organização
            </Botao>
          }
        />
        <Resultado
          resultado={resultado}
          nome={pedido.nomeFantasia}
          plano={planos.find((plano) => plano.id === pedido.planoId)?.nome ?? 'Do catálogo'}
          nomeDoAdmin={pedido.administrador.nome}
          emailDoAdmin={pedido.administrador.email}
          // Fechado o diálogo, a senha sai também da memória da tela.
          aoEsquecerAcesso={() => {
            const { primeiroAcesso: _, ...semAcesso } = resultado;
            definirResultado(semAcesso);
          }}
        />
      </div>
    );
  }

  const avisos = (
    <>
      {perguntas.retomadaDeEncerrada && (
        <Faixa tom="atencao">{perguntas.retomadaDeEncerrada}</Faixa>
      )}
      {perguntas.contaExistente && <Faixa tom="atencao">{perguntas.contaExistente}</Faixa>}
      {travado && !semVaga && (
        <Faixa tom="atencao">
          A criação pode ter começado e não terminou. Os passos já feitos ficam; tentar de novo
          continua de onde parou, com o mesmo identificador.
        </Faixa>
      )}
      {/* O motivo que a API devolveu, quando ela devolveu um: o limite de espaços, por exemplo. */}
      {resultado && resultado.status !== 'ativo' && resultado.pendencias.length > 0 && (
        <ul className="operacao__pendencias" aria-label="Por que não terminou">
          {resultado.pendencias.map((pendencia) => (
            <li key={pendencia}>{pendencia}</li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <div className="operacao">
      <CabecalhoDeTela
        titulo="Nova organização"
        apoio="Cria o cliente inteiro: o acesso, o cadastro, o atendimento e o administrador."
      />

      <div className="operacao__corpo">
        {/*
          O cartão de apresentação ocupa o TOPO, na largura toda, com os passos
          lado a lado; o formulário fica embaixo dele. Enquanto cria, o cartão
          de apresentação vira o PROGRESSO: o tempo correndo e os passos em
          andamento. É o mesmo cartão, e não um modal, para que o formulário
          continue à vista com o que foi pedido.
        */}
        <aside
          className={
            enviando
              ? 'operacao__apresentacao operacao__apresentacao--criando'
              : 'operacao__apresentacao'
          }
          aria-label="O que será criado"
          aria-busy={enviando}
        >
          <div className="operacao__intro">
            <img className="operacao__simbolo" src="/marca/simbolo-cliqcrm.png" alt="" />
            {enviando ? (
              <>
                <h2 className="operacao__chamada">
                  Criando {pedido?.nomeFantasia || 'a organização'}
                </h2>
                <p className="operacao__apoio">
                  Pode levar um minuto. Não feche esta tela.{' '}
                  <span className="operacao__cronometro">{segundos} s</span>
                </p>
                <div className="operacao__barra" aria-hidden="true" />
              </>
            ) : (
              <>
                <h2 className="operacao__chamada">Uma organização nova, pronta para atender</h2>
                <p className="operacao__apoio">
                  Em um minuto, o cliente recebe tudo o que precisa para começar. Cada passo fica
                  registrado; se algo travar no meio, a tentativa seguinte retoma os passos já
                  feitos.
                </p>
              </>
            )}
          </div>
          <ol className="operacao__passos">
            {PASSOS.map((passo) => (
              <li key={passo.nome}>
                <strong>{passo.nome}</strong>
                <span>{passo.texto}</span>
              </li>
            ))}
          </ol>
        </aside>

        {consultaDosPlanos.isPending ? (
          <Carregando forma="ficha" rotulo="Carregando os planos do catálogo" />
        ) : consultaDosPlanos.error && planos.length === 0 ? (
          <Erro erro={consultaDosPlanos.error} aoRepetir={() => void consultaDosPlanos.refetch()} />
        ) : planos.length === 0 ? (
          // O "Padrão" de antes do catálogo não tem preço: sem outro, não há o que contratar.
          <Vazio
            titulo="Nenhum plano para contratar"
            porque="A criação pede um plano do catálogo com preço. Cadastre um na aba Planos, em Organizações."
            {...(aoIrParaPlanos
              ? { acao: { rotulo: 'Ir para os planos', aoClicar: aoIrParaPlanos } }
              : {})}
          />
        ) : (
          <FormularioComContrato
            key={versao}
            planos={planos}
            segmentos={SEGMENTOS}
            enviando={enviando}
            falha={erro}
            hoje={hojeLocal()}
            travado={travado}
            aoCriar={(dados) => void criar(dados)}
            {...(aoVoltar ? { aoCancelar: aoVoltar } : {})}
            aoMudarEmailDoAdministrador={() => esquecerPergunta('contaExistente')}
            aoMudarIdentificador={() => esquecerPergunta('retomadaDeEncerrada')}
            avisos={avisos}
            bloqueado={
              api === null
                ? 'Sem conexão agora. Tente de novo em instantes.'
                : semVaga
                  ? 'O cadastro não tem vaga: tentar de novo recebe a mesma recusa.'
                  : null
            }
            rotuloDoEnvio={textoDoEnvio(travado, perguntas)}
            outraAcao={
              travado && !enviando ? (
                <Botao
                  variante="fantasma"
                  title={`A organização "${pedido?.identificador ?? ''}" fica pela metade, e só você ou outro Super Admin a retomam.`}
                  onClick={recomecar}
                >
                  Começar outra
                </Botao>
              ) : null
            }
          />
        )}
      </div>

      {/* Completar é do Super Admin, como criar (emenda de 26/10 ao ADR-0025). */}
      {sessao.estado.nome === 'autenticado' && podeCriarOrganizacaoComo(sessao.estado.me) && (
        <CompletarConfiguracao />
      )}
    </div>
  );
}

function Resultado({
  resultado,
  nome,
  plano,
  nomeDoAdmin,
  emailDoAdmin,
  aoEsquecerAcesso,
}: {
  resultado: RevendaProvisionada;
  nome: string;
  plano: string;
  nomeDoAdmin: string;
  emailDoAdmin: string;
  aoEsquecerAcesso: () => void;
}): ReactNode {
  const segundos = Math.max(1, Math.round(resultado.duracaoMs / 1000));
  /*
   * Como o administrador entra, guardado na montagem: ao fechar o diálogo a
   * senha sai da memória (`aoEsquecerAcesso`), mas o resumo continua dizendo
   * POR ONDE ela foi entregue.
   */
  const [via] = useState(resultado.primeiroAcesso?.via);
  const comoEntra =
    via === 'senha_provisoria'
      ? 'Recebeu uma senha provisória, que troca no primeiro acesso.'
      : via === 'email'
        ? `Recebeu em ${emailDoAdmin} um link para definir a senha.`
        : via === 'ja_tinha_conta'
          ? 'Já tinha conta no CliqCRM e entra com a senha que usa.'
          : 'Administra a organização a partir de agora.';
  /*
   * O primeiro acesso do administrador, no MESMO diálogo do convite de pessoa:
   * com senha provisória, ela aparece uma vez, com o botão de copiar, e some
   * ao fechar. Sem remetente de e-mail, é como o administrador entra
   * (provisório, até o envio de e-mail estar ligado).
   */
  return (
    <>
      {/*
        O diálogo fica FORA do resumo: o resumo entra com animação de
        `transform`, e um ancestral com `transform` vira a referência do véu
        `position: fixed` — o fundo escuro cobria só o resumo, e não a tela.
      */}
      {resultado.primeiroAcesso && (
        <PrimeiroAcessoDaPessoa
          nome={nomeDoAdmin}
          acesso={resultado.primeiroAcesso}
          aoFechar={aoEsquecerAcesso}
        />
      )}
      <section className="operacao__resultado" aria-labelledby="operacao-criada">
        <header className="operacao__sucesso">
          <span className="operacao__selo-ok" aria-hidden="true">
            <Visto />
          </span>
          <div className="operacao__sucesso-texto">
            <h2 id="operacao-criada" className="operacao__titulo">
              {nome} foi criada
            </h2>
            <p className="operacao__apoio">Pronta para atender. A criação levou {segundos} s.</p>
          </div>
        </header>

        <div className="operacao__resumo">
          <section className="operacao__cartao" aria-labelledby="operacao-dados">
            <h3 id="operacao-dados" className="operacao__subtitulo">
              A organização
            </h3>
            <dl className="operacao__dados">
              <div>
                <dt>Nome</dt>
                <dd>{nome}</dd>
              </div>
              <div>
                <dt>Identificador</dt>
                <dd>
                  <code className="operacao__codigo">{resultado.slug}</code>
                </dd>
              </div>
              <div>
                <dt>Plano</dt>
                <dd>{plano}</dd>
              </div>
            </dl>
          </section>

          <section className="operacao__cartao" aria-labelledby="operacao-admin">
            <h3 id="operacao-admin" className="operacao__subtitulo">
              Quem administra
            </h3>
            <div className="operacao__pessoa">
              <Avatar nome={nomeDoAdmin} tamanho={36} />
              <div className="operacao__pessoa-texto">
                <strong>{nomeDoAdmin}</strong>
                <span>{emailDoAdmin}</span>
              </div>
            </div>
            <p className="operacao__apoio">{comoEntra}</p>
          </section>

          <section
            className="operacao__cartao operacao__cartao--largo"
            aria-labelledby="operacao-feito"
          >
            <h3 id="operacao-feito" className="operacao__subtitulo">
              O que foi criado
            </h3>
            <ol className="operacao__feitos">
              {PASSOS.map((passo) => (
                <li key={passo.nome}>
                  <span className="operacao__feito-visto" aria-hidden="true">
                    <Visto />
                  </span>
                  <strong>{passo.nome}</strong>
                  <span>{passo.texto}</span>
                </li>
              ))}
            </ol>
          </section>

          {resultado.pendencias.length > 0 && (
            <section
              className="operacao__cartao operacao__cartao--largo operacao__cartao--atencao"
              aria-labelledby="operacao-falta"
            >
              <h3 id="operacao-falta" className="operacao__subtitulo">
                O que ainda falta fazer
              </h3>
              <ul className="operacao__pendencias">
                {resultado.pendencias.map((pendencia) => (
                  <li key={pendencia}>{pendencia}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </section>
    </>
  );
}

/** Como cada item aparece para quem lê: o que a organização ganha, e não o nome técnico. */
const NOME_DO_ITEM: Record<RevendaCompletada['itens'][number]['item'], string> = {
  integracao_do_atendimento: 'Ligação com o atendimento',
  atendentes: 'Administradores como atendentes',
  aviso_do_cadastro: 'Cadastro chegando ao CliqCRM (funil, clientes, tarefas)',
  aviso_do_atendimento: 'Conversas chegando ao CliqCRM',
};

const NOME_DO_RESULTADO = { aplicado: 'feito agora', dispensado: 'já estava', falhou: 'falhou' };

/**
 * Completar uma organização criada antes de a criação fazer tudo sozinha
 * (02/10/2026): faz só o que faltar, e rodar de novo é seguro.
 */
function CompletarConfiguracao(): ReactNode {
  const api = useApi();
  const [slug, definirSlug] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<RevendaCompletada | null>(null);

  async function completar(): Promise<void> {
    if (!api || !slug.trim()) return;
    definirEnviando(true);
    definirErro(null);
    definirResultado(null);
    try {
      definirResultado(await api.completarRevenda({ corpo: { slug: slug.trim() } }));
    } catch (falha) {
      definirErro(
        recadoDeErro(falha, {
          generica: 'Não foi possível completar agora. Tente de novo.',
          porCodigo: { NAO_ENCONTRADO: 'Nenhuma organização ativa com esse identificador.' },
        }),
      );
    } finally {
      definirEnviando(false);
    }
  }

  return (
    <section className="operacao__grupo" aria-labelledby="operacao-completar">
      <h2 id="operacao-completar" className="operacao__subtitulo">
        Completar configuração
      </h2>
      <p className="operacao__apoio">
        Para organizações criadas antes de a criação fazer tudo sozinha: liga o atendimento, os
        administradores como atendentes e os avisos que trazem o cadastro e as conversas. Faz só o
        que faltar.
      </p>
      <form
        className="operacao__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          void completar();
        }}
      >
        <Campo
          rotulo="Identificador da organização"
          value={slug}
          maxLength={32}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => definirSlug(e.target.value.toLowerCase())}
        />
        <div className="operacao__acoes">
          <Botao
            type="submit"
            desabilitado={!slug.trim() || enviando || api === null}
            motivo={enviando ? 'Completando…' : 'Preencha o identificador.'}
          >
            {enviando ? 'Completando…' : 'Completar configuração'}
          </Botao>
        </div>
      </form>
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
      {resultado && (
        <ul className="operacao__pendencias" aria-label="O que foi feito">
          {resultado.itens.map((item) => (
            <li key={item.item}>
              <strong>{NOME_DO_ITEM[item.item]}</strong>: {NOME_DO_RESULTADO[item.resultado]}
              {item.resultado === 'falhou' ? ` (${item.detalhe})` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
